import type { RoomListing, RoomType } from '../../../shared/types.js';
import { ROOM_TYPES } from '../../../shared/types.js';
import type { AppSocket } from '../socket';
import { el, relativeTime, showToast } from '../dom';
import { navigate } from '../router';
import { t, tPlural } from '../i18n';
import { buildLocalePicker } from '../i18n/picker';

export interface HomePageDeps {
  socket: AppSocket;
  rooms: RoomListing[] | null; // null = still loading
}

const TYPE_ICON: Record<RoomType, string> = {
  games: '🎮',
  movies: '🎬',
  anime: '🌸',
};

function typeLabel(type: RoomType): string {
  return t(`type.${type}` as 'type.games');
}

export function buildHomePage(deps: HomePageDeps): HTMLElement {
  const layout = el('div', 'home-layout');
  layout.appendChild(buildHomeHeader());
  const grid = el('div', 'home-grid');
  grid.appendChild(buildCreateForm(deps));
  grid.appendChild(buildRoomList(deps));
  layout.appendChild(grid);
  return layout;
}

function buildHomeHeader(): HTMLElement {
  const h = el('header', 'home-header');
  const top = el('div', 'home-header-top');
  top.appendChild(buildLocalePicker());
  h.appendChild(top);
  h.appendChild(el('div', 'home-title', t('home.title')));
  h.appendChild(el('div', 'home-subtitle', t('home.subtitle')));
  return h;
}

function buildCreateForm(deps: HomePageDeps): HTMLElement {
  const card = el('section', 'home-card create-card');
  card.appendChild(el('h2', 'card-h', t('home.create')));

  const form = el('form', 'create-form') as HTMLFormElement;

  const titleLabel = el('label', 'form-row');
  titleLabel.appendChild(el('span', 'form-label', t('home.titleLabel')));
  const title = document.createElement('input');
  title.className = 'form-input';
  title.type = 'text';
  title.placeholder = t('home.titlePlaceholder');
  title.maxLength = 60;
  title.required = true;
  titleLabel.appendChild(title);
  form.appendChild(titleLabel);

  const typeWrap = el('div', 'form-row');
  typeWrap.appendChild(el('span', 'form-label', t('home.typeLabel')));
  const radios = el('div', 'type-radios');
  let selectedType: RoomType = 'games';
  for (const ty of ROOM_TYPES) {
    const lbl = el('label', `type-radio ${ty === selectedType ? 'on' : ''}`);
    const r = document.createElement('input');
    r.type = 'radio';
    r.name = 'type';
    r.value = ty;
    r.checked = ty === selectedType;
    r.addEventListener('change', () => {
      selectedType = ty;
      for (const node of radios.querySelectorAll('.type-radio')) node.classList.remove('on');
      lbl.classList.add('on');
    });
    lbl.appendChild(r);
    lbl.appendChild(el('span', 'type-icon', TYPE_ICON[ty]));
    lbl.appendChild(el('span', 'type-label-text', typeLabel(ty)));
    radios.appendChild(lbl);
  }
  typeWrap.appendChild(radios);
  form.appendChild(typeWrap);

  const listedRow = el('label', 'form-row form-row-inline');
  const cb = document.createElement('input');
  cb.type = 'checkbox';
  cb.checked = true;
  cb.className = 'form-checkbox';
  listedRow.appendChild(cb);
  const listedText = el('span', 'form-label-inline');
  listedText.appendChild(document.createTextNode(t('home.listed') + '  '));
  listedText.appendChild(el('span', 'form-hint', t('home.listedHint')));
  listedRow.appendChild(listedText);
  form.appendChild(listedRow);

  const submit = el('button', 'btn primary big create-btn', t('home.createButton')) as HTMLButtonElement;
  submit.type = 'submit';
  form.appendChild(submit);

  form.addEventListener('submit', (ev) => {
    ev.preventDefault();
    const v = title.value.trim();
    if (!v) {
      showToast(t('home.titleRequired'), 'warn');
      return;
    }
    submit.disabled = true;
    deps.socket.emit('createRoom', { title: v, type: selectedType, listed: cb.checked });
  });

  card.appendChild(form);
  return card;
}

function buildRoomList(deps: HomePageDeps): HTMLElement {
  const card = el('section', 'home-card list-card');
  const head = el('div', 'list-head');
  head.appendChild(el('h2', 'card-h', t('home.activeRooms')));

  const joinForm = el('form', 'join-form') as HTMLFormElement;
  const codeInput = document.createElement('input');
  codeInput.className = 'join-code-input';
  codeInput.placeholder = t('home.codePlaceholder');
  codeInput.maxLength = 6;
  codeInput.style.textTransform = 'uppercase';
  joinForm.appendChild(codeInput);
  const joinBtn = el('button', 'btn small', t('home.join')) as HTMLButtonElement;
  joinBtn.type = 'submit';
  joinForm.appendChild(joinBtn);
  joinForm.addEventListener('submit', (ev) => {
    ev.preventDefault();
    const code = codeInput.value.trim().toUpperCase();
    if (code.length < 4) return;
    navigate(`/r/${code}`);
  });
  head.appendChild(joinForm);
  card.appendChild(head);

  if (deps.rooms === null) {
    card.appendChild(el('div', 'placeholder', t('home.loading')));
  } else if (deps.rooms.length === 0) {
    card.appendChild(el('div', 'placeholder', t('home.noRooms')));
  } else {
    const list = el('ul', 'room-list');
    for (const r of deps.rooms) list.appendChild(buildRoomRow(r));
    card.appendChild(list);
  }
  return card;
}

function buildRoomRow(r: RoomListing): HTMLElement {
  const li = el('li', 'room-row');
  li.dataset.key = `room-${r.id}`;
  li.appendChild(el('span', `room-row-icon room-type-${r.type}`, TYPE_ICON[r.type]));

  const main = el('div', 'room-row-main');
  main.appendChild(el('div', 'room-row-title', r.title));
  const sub = el('div', 'room-row-sub');
  sub.appendChild(el('span', 'room-row-code', r.id));
  sub.appendChild(el('span', 'sep', '·'));
  sub.appendChild(el('span', 'room-row-count',
    tPlural(r.playerCount, 'home.players_one', 'home.players_many', 'home.players_few')));
  sub.appendChild(el('span', 'sep', '·'));
  sub.appendChild(el('span', 'room-row-age', relativeTime(r.createdAt)));
  main.appendChild(sub);
  li.appendChild(main);

  const join = el('button', 'btn primary small', t('home.join')) as HTMLButtonElement;
  join.addEventListener('click', () => navigate(`/r/${r.id}`));
  li.appendChild(join);
  return li;
}
